"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect } from "react";
export default function CoreServices() {
  const {
    ref: coreRef,
    isVisible: coreVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: advancedRef,
    isVisible: advancedVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: supportiveRef,
    isVisible: supportiveVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  const handleCalendlyClick = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  const coreServices = [{
    title: "Testosterone Optimization & Hormone Health",
    description: "For men with clinically low or suboptimal testosterone contributing to fatigue, low libido, muscle loss, or poor recovery. Our approach prioritizes physiologic dosing, safety, and regular monitoring.",
    link: "https://ironpeakmenshealth.com/testosterone-replacement-therapy-trt/",
    plan: "All plans",
    medCost: false
  }, {
    title: "Weight Loss & Metabolic Health (GLP-1 Therapy)",
    description: "Medical weight loss for men struggling with insulin resistance, appetite control, or stubborn fat despite effort. Includes physician oversight, metabolic labs, and long-term strategy—not crash dieting.",
    link: "https://ironpeakmenshealth.com/medical-weight-loss-for-men-glp-1-therapy-ironpeak-mens-health/",
    plan: "Ascent & Summit",
    medCost: true
  }, {
    title: "Erectile Dysfunction Treatment",
    description: "Evidence-based ED treatment focused on vascular health, hormones, and overall performance—not just symptom suppression.",
    link: "https://ironpeakmenshealth.com/erectile-dysfunction-treatment-in-texas/",
    plan: "Add-on to any plan",
    medCost: true
  }, {
    title: "Hair Loss Treatment",
    description: "Medical treatment plans designed to slow or reverse hair loss while minimizing side effects and preserving long-term scalp and hormonal health.",
    link: "https://ironpeakmenshealth.com/hair-loss-treatment-for-men-male-pattern-baldness-ironpeak-mens-health/",
    plan: "Ascent & Summit",
    medCost: true
  }, {
    title: "Peptide Therapy",
    description: "Targeted peptide protocols for growth hormone support, body composition, and sexual performance. We prescribe Sermorelin, Ipamorelin, Tesamorelin, and PT-141—when clinically appropriate and tailored to your specific goals.",
    link: "#",
    plan: "Ascent & Summit",
    medCost: true
  }];
  const advancedServices = [{
    title: "Longevity & Aging Optimization",
    description: "For men who want to proactively protect energy, cognition, body composition, and health-span as they age—using data-driven strategies and medical oversight.",
    link: "https://ironpeakmenshealth.com/longevity-medicine-for-men-in-texas/",
    plan: "Summit",
    medCost: false
  }, {
    title: "Cognitive Performance Support",
    description: "Support for focus, motivation, mental clarity, and cognitive resilience—addressing sleep, hormones, metabolism, and nutrient status.",
    link: "https://ironpeakmenshealth.com/cognitive-optimization-for-men-in-texas/",
    plan: "Summit",
    medCost: false
  }, {
    title: "Personalized Performance Coaching",
    description: "One-on-one guidance to optimize training, nutrition, recovery, stress, and habits—integrated with your medical plan.",
    link: "https://ironpeakmenshealth.com/performance-coaching-for-men-in-texas/",
    plan: "Summit",
    medCost: false
  }, {
    title: "NAD+ Therapy",
    description: "NAD+ supports cellular energy production, cognitive function, and metabolic recovery. Administered as an injectable for maximum absorption—ideal for men focused on longevity, performance, and slowing the effects of aging.",
    link: "#",
    plan: "Ascent & Summit",
    medCost: false,
    planNote: "Pricing varies by plan"
  }];
  const supportiveServices = [{
    title: "Vitamin B12 Injections",
    description: "Methylcobalamin B12 injections for sustained energy, neurological support, and metabolic function. Especially beneficial for men experiencing fatigue, brain fog, or those on medications that deplete B12 levels.",
    link: "#",
    plan: "Ascent & Summit",
    medCost: false
  }, {
    title: "Lab Coordination (LabCorp/Quest)",
    description: "Physician-ordered labs through LabCorp and Quest Diagnostics—not consumer wellness panels. We order what's clinically relevant, interpret results in context, and monitor trends over time to guide your treatment.",
    link: "#",
    plan: "All plans",
    medCost: false
  }];
  return <div className="bg-background py-12">
      {}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={coreRef} className={`transition-all duration-700 ${coreVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-foreground text-center font-display text-3xl font-bold lg:text-4xl">
            Core Services
          </h2>
          <div className="mx-auto mt-4 flex justify-center">
            <Separator className="bg-muted-light h-px w-24" />
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {coreServices.map((service, i) => <Card key={i} data-index={i} className="border-muted-light shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader>
                  <CardTitle>{service.title}</CardTitle>
                  <span className="inline-block w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mt-1">
                    {service.plan}
                  </span>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                    
                      
                      {service.medCost && <p className="text-xs text-muted-foreground italic">Medication billed separately</p>}
                      {service.planNote && <p className="text-xs text-muted-foreground italic">{service.planNote}</p>}
                   
                    <div className="flex items-center gap-4 flex-wrap">
                      {service.link !== "#" && <Link to={service.link} className="text-primary inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary/80">
                          Learn more
                          <ArrowRight className="size-4" />
                        </Link>}
                      
                    </div>
                  </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-center">
        <Link to="/pricing" className="inline-flex items-center gap-1 text-primary font-medium">
          View all pricing plans <ArrowRight className="h-4 w-4 shrink-0" />
        </Link>
      </div>
      {}
      <div className="mx-auto mt-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={advancedRef} className={`transition-all duration-700 ${advancedVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-foreground text-center font-display text-3xl font-bold lg:text-4xl">
            Advanced Optimization
          </h2>
          <div className="mx-auto mt-4 flex justify-center">
            <Separator className="bg-muted-light h-px w-24" />
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
            {advancedServices.map((service, i) => <Card key={i} data-index={i} className="border-muted-light shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader>
                  <CardTitle>{service.title}</CardTitle>
                  <span className="inline-block w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mt-1">
                    {service.plan}
                  </span>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                    
                      {service.medCost && <p className="text-xs text-muted-foreground italic">Medication billed separately</p>}
                      {service.planNote && <p className="text-xs text-muted-foreground italic">{service.planNote}</p>}
                    
                    <div className="flex items-center gap-4 flex-wrap">
                      {service.link !== "#" && <Link to={service.link} className="text-primary inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary/80">
                          Learn more
                          <ArrowRight className="size-4" />
                        </Link>}
                      
                    </div>
                  </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    <div className="mt-6 flex justify-center">
      <Link to="/pricing" className="inline-flex items-center gap-1 text-primary font-medium">
        View all pricing plans <ArrowRight className="h-4 w-4 shrink-0" />
      </Link>
    </div>
      {/* Supportive Treatments */}
      <div className="mx-auto mt-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={supportiveRef} className={`transition-all duration-700 ${supportiveVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-foreground text-center font-display text-3xl font-bold lg:text-4xl">
            Supportive Treatments
          </h2>
          <div className="mx-auto mt-4 flex justify-center">
            <Separator className="bg-muted-light h-px w-24" />
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
            {supportiveServices.map((service, i) => <Card key={i} className="border-muted-light shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader>
                    <CardTitle>{service.title}</CardTitle>
                    <span className="inline-block w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mt-1">
                      {service.plan}
                    </span>
                  </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                    
                      {service.medCost && <p className="text-xs text-muted-foreground italic">Medication billed separately</p>}
                      {service.planNote && <p className="text-xs text-muted-foreground italic">{service.planNote}</p>}
                    
                    <div className="flex items-center gap-4 flex-wrap">
                      {service.link !== "#" && <Link to={service.link} className="text-primary inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary/80">
                          Learn more
                          <ArrowRight className="size-4" />
                        </Link>}
                     
                    </div>
                  </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    <div className="mt-6 flex justify-center">
      <Link to="/pricing" className="inline-flex items-center gap-1 text-primary font-medium">
        View all pricing plans <ArrowRight className="h-4 w-4 shrink-0" />
      </Link>
    </div>
      {}
      <div className="mx-auto mt-12 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ctaRef} className={`transition-all duration-700 ${ctaVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Card className="border-muted-light shadow-soft bg-primary-light-bg">
            <CardContent className="flex flex-col items-center justify-center space-y-6 py-12 text-center">
              <p className="text-foreground max-w-3xl text-base leading-relaxed md:text-lg">
                Ready to Get Started? If something feels off—and you're not getting the results you should despite effort—start with a medical consultation.
              </p>
              <Button onClick={handleCalendlyClick} className="inline-flex h-12 items-center justify-center rounded-sm bg-accent px-6 py-3 font-semibold text-accent-foreground transition-all duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-medium active:translate-y-0 active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40">
                Schedule a Consultation
              </Button>
              <p className="text-foreground/80 text-sm text-center">
                Texas-based telemedicine. Ongoing follow-up. Evidence-based care.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}